/**
 * 409 envelopes for the cross-user contact-method claim (identity-plan.md §7).
 *
 * One contact method identifies one person (D1), so a method another account
 * has *verified* means the two accounts are the same person. Adding it is
 * therefore the start of a user merge rather than a dead end:
 *
 *   POST /api/auth/contact-method/   -> 409 method_owned_by_another_account
 *   POST /api/users/merge/claim/     -> 409 not_claimable | claim_rate_limited
 *                                       (the latter with `retry_after`, #1666 S2)
 *
 * Like `mergeDenial.ts`, these shapes are deliberately NOT in vingapi's
 * OpenAPI document — FastAPI does not emit exception envelopes — so this
 * hand-written zod schema IS the frontend's contract for them. Parsing is
 * fail-closed: anything unrecognised yields `null` and the caller falls back
 * to its ordinary error handling.
 *
 * ENUMERATION CONSTRAINT. None of these envelopes name the other account, and
 * nothing built on them may imply it. `get_verify_methods_for_user` returns
 * the counterpart's entire list of verified emails and phones, so a
 * suggestion created from an unproven assertion would let anyone type an
 * address they happen to know and read every method on that account. vingapi's
 * `MethodOwnedByAnotherAccountError` carries only a noun ("email"/"phone") for
 * exactly this reason — its message is already correctly opaque, so surface it
 * rather than writing our own.
 */

import { AxiosError } from 'axios';
import { z } from 'zod';
import { convertKeysToCamel } from '@/lib/dataTransform';

/** Refusal of `POST /auth/contact-method/`: another account verified it. */
export const METHOD_OWNED = 'method_owned_by_another_account';
/**
 * Refusals of `POST /users/merge/claim/`.
 *
 * `not_claimable` is usually §7's state transition rather than an error: the
 * owner removed the method while the claim was pending, so it is unowned now
 * and an ordinary add simply succeeds. The user should just try adding it
 * again.
 */
export const CLAIM_REFUSALS = ['not_claimable', 'claim_rate_limited'] as const;
export type ClaimRefusalCode = (typeof CLAIM_REFUSALS)[number];

const MethodOwnedSchema = z.object({
  error: z.literal(METHOD_OWNED),
  message: z.string(),
  /** vingapi names the next step in the body; we don't hardcode the path. */
  claimEndpoint: z.string().optional(),
});

const ClaimRefusalSchema = z.object({
  error: z.enum(CLAIM_REFUSALS),
  message: z.string(),
  /**
   * `claim_rate_limited` (#1666 S2): seconds until the spent budget refills.
   * A malformed value is dropped (the copy says "later"), never a reason to
   * stop recognising the refusal itself.
   */
  retryAfter: z.number().int().nonnegative().optional().catch(undefined),
});

export interface MethodOwnedEnvelope {
  /** vingapi's own opaque wording — never says whose account it is. */
  message: string;
}

export interface ClaimRefusalEnvelope {
  code: ClaimRefusalCode;
  message: string;
  /** Seconds until another claim can send, when the refusal says (#1163). */
  retryAfter?: number;
}

/**
 * Unwrap FastAPI's `{"detail": {...}}` convention. Exported because every
 * error-envelope parser in this family (see `heldByLinkedContact.ts`,
 * `generateLinkFailure.ts`) must unwrap identically — a second copy is a
 * second place to drift. Falls back to the raw body when `detail` is absent
 * or a plain string (the default HTTPException shape), which then simply
 * fails the schema parse below.
 *
 * @param expectedStatus - HTTP status to match (default 409). The generate-link
 *   422 envelope uses the same `{"detail": {...}}` shape as the 409 family.
 */
export function unwrapConflictDetail(error: unknown, expectedStatus = 409): unknown {
  if (!(error instanceof AxiosError) || error.response?.status !== expectedStatus) return null;
  const rawData = error.response.data as { detail?: unknown } | undefined;
  return convertKeysToCamel(rawData?.detail ?? rawData);
}

/**
 * Is this add-contact-method failure the "another account verified it" 409?
 * Returns the envelope when so, else null — the caller then treats it as an
 * ordinary failure.
 */
export function parseMethodOwnedEnvelope(error: unknown): MethodOwnedEnvelope | null {
  const detail = unwrapConflictDetail(error);
  if (detail === null) return null;
  const parsed = MethodOwnedSchema.safeParse(detail);
  if (!parsed.success) return null;
  return { message: parsed.data.message };
}

/** Is this claim failure one of the two known refusals? */
export function parseClaimRefusal(error: unknown): ClaimRefusalEnvelope | null {
  const detail = unwrapConflictDetail(error);
  if (detail === null) return null;
  const parsed = ClaimRefusalSchema.safeParse(detail);
  if (!parsed.success) return null;
  return { code: parsed.data.error, message: parsed.data.message, retryAfter: parsed.data.retryAfter };
}
